import { formatTimeLogDateTimeLabel } from "@jugl-web/domain-resources/task-time-tracking/utils";
import { LogMode } from "@jugl-web/rest-api/tasks/models/TaskTimeLog/LogMode";
import { TaskTimeLogModel } from "@jugl-web/rest-api/tasks/models/TaskTimeLog/TaskTimeLogModel";
import { Menu, PlainButton } from "@jugl-web/ui-components";
import { cx, useAppVariant, useTranslations } from "@jugl-web/utils";
import { formatSecondsToDuration } from "@jugl-web/utils/date-time/formatSecondsToDuration";
import { differenceInSeconds } from "date-fns";
import { FC } from "react";
import { ManuallyChangedLogIndicator } from "../../../ManuallyChangedLogIndicator";
import { TimerTextWrapper } from "../../../TimerTextWrapper";
import { ReactComponent as ChevronRightIcon } from "../../assets/chevron-right.svg";
import { ReactComponent as MoreIcon } from "../../assets/more.svg";
import { ReactComponent as PenIcon } from "../../assets/pen.svg";
import { ReactComponent as TrashIcon } from "../../assets/trash.svg";
import { TimeTrackingHistoryProps } from "../../types";

interface HistoryListItemProps {
  timeLog: TaskTimeLogModel;
  onEditTimeLog?: TimeTrackingHistoryProps["onEditTimeLog"];
  onDeleteTimeLog?: TimeTrackingHistoryProps["onDeleteTimeLog"];
}

export const HistoryListItem: FC<HistoryListItemProps> = ({
  timeLog,
  onEditTimeLog,
  onDeleteTimeLog,
}) => {
  const { isMobile } = useAppVariant();

  const { t } = useTranslations();

  const isInteractive = isMobile && !!onEditTimeLog;

  const formattedDuration = formatSecondsToDuration(
    differenceInSeconds(timeLog.endDate || new Date(), timeLog.startDate)
  );

  const formattedDateTimeLabel = formatTimeLogDateTimeLabel(timeLog);

  return (
    <div
      className={cx(
        "flex h-10 items-center justify-between gap-2 px-2",
        isInteractive && "jugl__mobile-pressable"
      )}
      role={isInteractive ? "button" : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      onClick={isInteractive ? () => onEditTimeLog?.(timeLog) : undefined}
    >
      <div className="flex items-center gap-3">
        <TimerTextWrapper>
          <span className="text-dark font-secondary text-sm font-semibold leading-[140%] tracking-[0.14px]">
            {formattedDuration}
          </span>
        </TimerTextWrapper>

        <div className="text-dark-600 font-secondary flex items-center text-xs leading-[150%]">
          {formattedDateTimeLabel}
        </div>
      </div>
      <div className="flex items-center gap-1">
        {timeLog.logMode !== LogMode.Auto && <ManuallyChangedLogIndicator />}
        {isMobile ? (
          <ChevronRightIcon className="shrink-0" />
        ) : (
          <Menu
            placement="bottom-end"
            sections={[
              [
                {
                  id: "edit",
                  icon: <PenIcon />,
                  label: t({
                    id: "tasks-page.edit-time-log",
                    defaultMessage: "Edit Log",
                  }),
                  isHidden: !onEditTimeLog,
                  onSelect: (_, close) => {
                    onEditTimeLog?.(timeLog);
                    close();
                  },
                },
                {
                  id: "delete",
                  icon: <TrashIcon />,
                  label: t({
                    id: "tasks-page.delete-time-log",
                    defaultMessage: "Delete Log",
                  }),
                  isHidden: !onDeleteTimeLog,
                  onSelect: (_, close) => {
                    onDeleteTimeLog?.(timeLog);
                    close();
                  },
                },
              ],
            ]}
            renderTrigger={({ Trigger, triggerRef, isOpen }) => (
              <Trigger
                ref={triggerRef}
                as={PlainButton}
                className={cx(
                  "hover:bg-grey-200 shrink-0 rounded-md transition-colors",
                  isOpen && "bg-grey-200"
                )}
              >
                <MoreIcon />
              </Trigger>
            )}
          />
        )}
      </div>
    </div>
  );
};
